import { AGENTS, HOUSES } from '../config/competition'
import { signedCompactMoney, signedPct } from '../core/format'
import type { HouseStanding } from '../core/types'
import { AgentAvatar } from './primitives/Media'
import { Panel } from './primitives/Panel'
import { Standby } from './primitives/Standby'

/** Head-to-head, as a tug of war rather than two numbers side by side.
    The bar is a diverging encoding: one warm pole, one cool pole, neutral
    midpoint, and the midpoint means a dead heat. */
export function HouseWar({ houses, standby }: { houses: HouseStanding[]; standby: boolean }) {
  if (standby) {
    return (
      <Panel title="HOUSE WAR" className="housewar">
        <Standby note="pump.fun against fomo, scored as one number." />
      </Panel>
    )
  }

  const pump = houses.find((h) => h.house === 'pumpfun')
  const fomo = houses.find((h) => h.house === 'fomo')
  if (!pump || !fomo) return null

  const d = Math.max(-1, Math.min(1, pump.dominance))
  const leader = d === 0 ? null : d > 0 ? pump : fomo
  const share = Math.abs(d) * 50

  return (
    <Panel
      title="HOUSE WAR"
      note={
        leader ? (
          <span className="u-num">
            {HOUSES[leader.house].name} +{(Math.abs(d) * 100).toFixed(1)} EDGE
          </span>
        ) : (
          <span className="u-num">DEAD HEAT</span>
        )
      }
      className="housewar"
    >
      <div className="hw">
        <HouseSide side="left" h={pump} />
        <div className="hw__bar">
          <div className="hw__track">
            <span
              className="hw__fill hw__fill--pump"
              style={{ width: `${d > 0 ? share : 0}%`, right: '50%' }}
            />
            <span
              className="hw__fill hw__fill--fomo"
              style={{ width: `${d < 0 ? share : 0}%`, left: '50%' }}
            />
            <span className="hw__ticks" aria-hidden="true">
              {Array.from({ length: 5 }, (_, i) => (
                <i key={i} />
              ))}
            </span>
            <span className="hw__mid" />
          </div>
          <div className="hw__scale">
            <span className="u-label">PUMP.FUN EDGE</span>
            <span className="u-label">FOMO EDGE</span>
          </div>
        </div>
        <HouseSide side="right" h={fomo} />
      </div>
    </Panel>
  )
}

function HouseSide({ h, side }: { h: HouseStanding; side: 'left' | 'right' }) {
  const meta = HOUSES[h.house]
  const roster = AGENTS.filter((a) => a.house === h.house)
  return (
    <div className={`hw__side hw__side--${side}`} style={{ borderColor: meta.color }}>
      <div className="hw__name" style={{ color: meta.color }}>
        {meta.name}
      </div>
      <div className={`hw__pnl u-num ${h.pnl >= 0 ? 'u-up' : 'u-down'}`}>
        {signedCompactMoney(h.pnl)}
      </div>
      <div className="hw__sub">
        <span className={h.roiPct >= 0 ? 'u-up' : 'u-down'}>{signedPct(h.roiPct)}</span>
        <span className="u-flat">{h.tradeCount} FILLS</span>
      </div>
      <div className="hw__roster">
        {roster.map((a) => (
          <span key={a.id} className="hw__member">
            <AgentAvatar agentId={a.id} size={16} />
            {a.name}
          </span>
        ))}
      </div>
    </div>
  )
}
